import { type FocusEvent, type KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import CloseLine from '@/assets/icons/close_line.svg?component';
import DeleteLine from '@/assets/icons/delete_line.svg?component';
import { Button } from '@/components/atoms/Buttons';
import { Spinner } from '@/components/atoms/Spinner';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';

import styles from './TwoStepDeletionButton.module.scss';

const TIMER_DELAY = 5000;

interface TwoStepDeletionButtonProps {
    onConfirm: () => void;
    disabled?: boolean;
    isDeleting?: boolean;
}

export default function TwoStepDeletionButton(props: TwoStepDeletionButtonProps) {
    const { onConfirm, disabled = false, isDeleting = false } = props;

    const [showSecondStep, setShowSecondStep] = useState<boolean>(false);
    const [hasTransitioned, setHasTransitioned] = useState<boolean>(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const deleteRef = useRef<HTMLButtonElement>(null);
    const confirmRef = useRef<HTMLButtonElement>(null);
    // The step that swaps in takes focus only when the reader's focus was on the one leaving.
    const focusNext = useRef(false);
    const hovered = useRef(false);
    const focused = useRef(false);

    const { t } = useTranslation();

    const cleanTimer = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    };

    useEffect(() => {
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    useEffect(() => {
        if (!focusNext.current) return;
        focusNext.current = false;
        (showSecondStep ? confirmRef : deleteRef).current?.focus();
    }, [showSecondStep]);

    // The confirm step lapses only while nobody is at it: not focused, not under the pointer.
    const armTimer = () => {
        cleanTimer();
        if (hovered.current || focused.current) return;
        timerRef.current = setTimeout(() => setShowSecondStep(false), TIMER_DELAY);
    };

    const handleFirstStepClick = () => {
        setHasTransitioned(true);
        focusNext.current = true;
        setShowSecondStep(true);
        armTimer();
    };

    const handleCancel = () => {
        cleanTimer();
        focusNext.current = true;
        setShowSecondStep(false);
    };

    const handleConfirm = () => {
        onConfirm();
        cleanTimer();
    };

    const handlePointer = (inside: boolean) => {
        hovered.current = inside;
        if (showSecondStep) armTimer();
    };

    const handleFocus = (inside: boolean) => (event: FocusEvent<HTMLDivElement>) => {
        // Moving between the confirm and cancel buttons keeps focus inside.
        if (!inside && event.currentTarget.contains(event.relatedTarget)) return;
        focused.current = inside;
        if (showSecondStep) armTimer();
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== 'Escape' || !showSecondStep) return;
        event.stopPropagation();
        handleCancel();
    };

    return (
        <div
            onMouseEnter={() => handlePointer(true)}
            onMouseLeave={() => handlePointer(false)}
            onFocus={handleFocus(true)}
            onBlur={handleFocus(false)}
            onKeyDown={handleKeyDown}>
            {showSecondStep ? (
                <div
                    className={classNames(styles.secondStepContainer, {
                        [styles.fadeIn]: hasTransitioned,
                    })}>
                    <Button
                        ref={confirmRef}
                        variant="dangerSolid"
                        iconOnly
                        aria-label={t('harvestReport.twoStepDeletionButton.confirm')}
                        tooltip={t('harvestReport.twoStepDeletionButton.confirm')}
                        tooltipPlacement="bottom"
                        onClick={handleConfirm}
                        disabled={disabled || isDeleting}
                        icon={
                            isDeleting ? (
                                <Spinner className={styles.spinner} />
                            ) : (
                                <SvgIcon icon={DeleteLine} color="currentColor" size="small" />
                            )
                        }
                    />
                    <Button
                        variant="secondary"
                        iconOnly
                        aria-label={t('harvestReport.twoStepDeletionButton.cancel')}
                        tooltip={t('harvestReport.twoStepDeletionButton.cancel')}
                        tooltipPlacement="bottom"
                        onClick={handleCancel}
                        disabled={disabled || isDeleting}
                        icon={
                            <SvgIcon
                                icon={CloseLine}
                                color="currentColor"
                                size="custom"
                                customSize={12}
                            />
                        }
                    />
                </div>
            ) : (
                <Button
                    ref={deleteRef}
                    variant="danger"
                    className={classNames(styles.firstStepButton, {
                        [styles.fadeIn]: hasTransitioned,
                        [styles.fadeOut]: showSecondStep,
                    })}
                    onClick={handleFirstStepClick}
                    disabled={disabled || isDeleting}
                    icon={<SvgIcon icon={DeleteLine} color="currentColor" size="small" />}>
                    {t('common.actions.delete')}
                </Button>
            )}
        </div>
    );
}
